import React, { useState, useEffect, useImperativeHandle } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router";
import { Quote, OptionType } from "../../types/Quote";
import {
  GetProjectByIdApiResponse,
  V1AccountResource,
  useCreateQuoteMutation,
  useListServiceCategorysForAccountQuery,
  useListVendorsForAccountQuery,
  useUpdateQuoteMutation,
  V1QuoteResource,
} from "@generated";
import { RootState } from "@reducers/rootReducer";
import { useSelector } from "react-redux";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { handleAlert, snakeToText } from "@utils/helperFunctions";
import { FormNumberFieldV2 } from "@components/FormsV2/FormNumberFieldV2";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import MultipleLineFieldV2 from "@components/FormsV2/MultipleLineFieldV2";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import { Select2V2 } from "@components/FormsV2";
import AddVendorSlideout from "../AddVendorSlideout";
import DropdownApi from "@components/FormsV2/DropdownApi/DropdownApi";
import {
  getFormattedQuote,
  calculatePrice,
  calculateMarkup,
} from "../../quoteHelpers/quoteHelpers";

interface Props {
  quote: Quote;
  setQuote: (quote: Quote) => void;
  setPreviousQuote: (quote: Quote) => void;
  project_id: number;
  account: {
    data: V1AccountResource;
  };
  permissions: { quotes: string; vendors: string };
  project: GetProjectByIdApiResponse;
  revenueCalculationLabel: string;
  label: string;
}

const QuoteForm = React.forwardRef(
  (
    {
      quote,
      setQuote,
      setPreviousQuote,
      project_id,
      account,
      permissions,
      project,
      revenueCalculationLabel,
      label = "Vendor Quote",
    }: Props,
    ref
  ) => {
    // Redux / ENV
    const { accountSlug } = useSelector((state: RootState) => state.slug);
    // Location States / React Router
    const location = window.location.href;
    const lastWordInLocation = location.substr(location.lastIndexOf("/") + 1);
    let navigate = useNavigate();
    const historyLocation = useLocation();
    const marginVsMarkup =
      quote?.revenueCalculationLabel || revenueCalculationLabel;

    // Get Service Categories
    const { data: lobs, isLoading: lobsLoading } =
      useListServiceCategorysForAccountQuery({
        slug: accountSlug,
      });

    // Get Vendors
    const { data: vendors, refetch: vendorsRefetch } =
      useListVendorsForAccountQuery({
        slug: accountSlug,
      });

    // Alert States
    const [errorMessages, setErrorMessages] = useState<string | string[]>(
      "Something went wrong! Your changes could not be saved at this time."
    );
    const [showFailAlert, setShowFailAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState(
      "Project Expense saved successfully!"
    );
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [vendorNameInvalid, setVendorNameInvalid] = useState(false);
    const [lobInvalid, setLobInvalid] = useState(false);
    const [slideoutOpen, setSlideoutOpen] = useState(false);
    const [newVendor, setNewVendor] = useState<OptionType>({
      label: "",
      value: "",
    });
    const [priceOverride, setPriceOverride] = useState(false);
    const [mrrOverride, setMrrOverride] = useState(false);

    // Mutations
    const [createVendorQuote] = useCreateQuoteMutation();
    const [updateVendorQuote] = useUpdateQuoteMutation();

    useEffect(() => {
      if (historyLocation?.state?.new) {
        setSuccessMessage(`${label} created successfully!`);
        setShowSuccessAlert(true);
      }
    }, []);

    useImperativeHandle(ref, () => ({
      saveQuote,
    }));

    const billingFrequencyOptions: OptionType[] = [
      "one_time",
      "monthly",
      "quarterly",
      "yearly",
    ].map((bf) => {
      return {
        value: bf,
        label: bf === "one_time" ? "No" : snakeToText(bf),
      } as OptionType;
    });

    const lobOptions = (lobs?.data || []).map((lob) => {
      return {
        label:
          lob?.attributes?.["nested-name"] || lob?.attributes?.["name"] || "",
        value: lob?.id?.toString() || "",
      };
    });

    const vendorOptions = (vendors?.data || []).map((vendor) => {
      return {
        label: vendor?.attributes?.["name"] || "",
        value: vendor?.id?.toString() || "",
      };
    });

    const clearForm = () => {
      setQuote({
        id: "0",
        description: "",
        oneTimeCost: "0.0",
        oneTimeRevenue: "0.0",
        markup: "",
        mrrMarkup: "",
        recurringCost: "0.0",
        recurringRevenue: "0.0",
        billingFrequency: {
          value: "one_time",
          label: "No",
        },
        billingMethod: {
          value: "time_and_materials",
          label: "Time and Materials",
        },
      });
    };

    const isRecurring = quote?.billingFrequency?.value !== "one_time";

    const saveQuote = (saveAndAddClicked) => {
      if (!quote?.vendor?.value) {
        setVendorNameInvalid(true);
        return;
      }
      if (!quote?.lob?.value) {
        setLobInvalid(true);
        return;
      }
      let data = {
        type: "quotes",
        attributes: {
          description: quote?.description || "",
          "billing-frequency":
            String(quote?.billingFrequency?.value) || "one_time",
          "pricing-model": quote?.billingMethod?.value || "time_and_materials",
          "revenue-calculation": marginVsMarkup,
          cost: Number(quote.oneTimeCost || 0),
          markup: Number(quote.markup || 0),
          "price-override": priceOverride ? quote.oneTimeRevenue : null,
          "recurring-cost": isRecurring
            ? Number(quote.recurringCost || 0)
            : undefined,
          "mrr-markup": isRecurring ? Number(quote.mrrMarkup || 0) : undefined,
          "mrr-override":
            isRecurring && mrrOverride
              ? Number(quote.recurringRevenue || 0)
              : null,
        },
        relationships: {
          project: { data: { type: "projects", id: project_id } },
          vendor: {
            data: { type: "vendors", id: Number(quote?.vendor?.value) },
          },
          lob: { data: { type: "lobs", id: Number(quote?.lob?.value) } },
        },
      } as V1QuoteResource;

      if (lastWordInLocation !== "new") {
        updateVendorQuote({
          slug: accountSlug,
          id: Number(quote?.id),
          body: { data: { ...data, id: Number(quote?.id) } },
        })
          .unwrap()
          .then((response) => {
            if (response?.data?.id) {
              const updated = {
                ...getFormattedQuote(response?.data, vendors?.data, lobs?.data),
                vendor: quote?.vendor,
                lob: quote?.lob,
              };
              setPreviousQuote(updated);
              setQuote(updated);
              setSuccessMessage(`${label} updated.`);
              setShowSuccessAlert(true);
            }
          })
          .catch((err) => {
            let msg = err?.data?.errors?.[0]?.detail;
            setErrorMessages(
              msg
                ? msg
                : "Something went wrong. Your changes could not be saved at this time."
            );
            setShowFailAlert(true);
          });
      } else {
        createVendorQuote({
          slug: accountSlug,
          body: { data },
        })
          .unwrap()
          .then((response) => {
            if (response?.data?.id) {
              if (saveAndAddClicked) {
                clearForm();
                setSuccessMessage(`${label} created successfully!`);
                setShowSuccessAlert(true);
              } else {
                const updated = {
                  ...getFormattedQuote(
                    response?.data,
                    vendors?.data,
                    lobs?.data
                  ),
                  vendor: quote?.vendor,
                  lob: quote?.lob,
                };
                setQuote(updated);
                navigate(`/projects/${project_id}/quotes/edit/${updated?.id}`, {
                  state: { new: true, quote: updated },
                });
              }
            }
          })
          .catch((err) => {
            let msg = err?.data?.errors?.[0]?.detail;
            setErrorMessages(
              msg ||
                "Something went wrong. Your changes could not be saved at this time."
            );
            setShowFailAlert(true);
          });
      }
    };

    useEffect(() => {
      if (newVendor.value) {
        setQuote({
          ...quote,
          vendor: vendorOptions?.find((v) => v.value === newVendor.value),
        });
        setNewVendor({ label: "", value: "" });
      }
    }, [vendors]);

    if (!account || !lobs || lobsLoading) {
      return <SmallSpinner />;
    }

    const disableFields =
      (["view", "create"].includes(permissions.quotes) ||
        project?.data?.attributes?.status !== "building") &&
      lastWordInLocation !== "new";
    const lockPricingFields =
      permissions.quotes === "view" ||
      project?.data?.attributes?.status !== "building" ||
      quote?.useServicePricing;
    return (
      <Form
        style={{ marginLeft: "16px" }}
        className="quoteForm"
        onSubmit={(e) => e.preventDefault()}
      >
        {handleAlert(
          showFailAlert,
          errorMessages,
          setShowFailAlert,
          "warning",
          ToastAlert
        )}
        {handleAlert(
          showSuccessAlert,
          successMessage,
          setShowSuccessAlert,
          "success",
          ToastAlert
        )}
        <section>
          <AddVendorSlideout
            slideoutOpen={slideoutOpen}
            setSlideoutOpen={setSlideoutOpen}
            setNewVendor={setNewVendor}
            refetch={vendorsRefetch}
          />
          <div className="instructionalText bold">Vendor / Quote Info</div>
          <Row style={{ alignItems: "center", marginRight: "1px" }}>
            <Col sm={10}>
              <DropdownApi
                label="* Vendor Name"
                value={
                  quote?.vendor || {
                    value: 0,
                    label: "Search and select vendor name",
                  }
                }
                required={true}
                isInvalid={vendorNameInvalid}
                onChange={(e) => {
                  if (e) {
                    setVendorNameInvalid(false);
                    setQuote({ ...quote, vendor: e as OptionType });
                  }
                }}
                getDataHook={useListVendorsForAccountQuery}
                isDisabled={disableFields}
              />
            </Col>
            <Col sm={2}>
              {["create", "manage"].includes(permissions.vendors) && (
                <Button
                  className="btnSeawhite"
                  onClick={() => setSlideoutOpen(true)}
                >
                  Add Vendor
                </Button>
              )}
            </Col>
          </Row>
          <Row style={{ marginRight: "1px" }}>
            <Col sm={12}>
              <MultipleLineFieldV2
                id="quoteDescription"
                label="Description"
                value={quote?.description}
                onChange={(e) =>
                  setQuote({ ...quote, description: e.target.value })
                }
                disabled={disableFields}
                placeholder="Enter a high-level description of the work being performed"
              />
            </Col>
          </Row>
          <Row style={{ marginRight: "1px" }}>
            <Col sm={6}>
              <RequiredReactSelect
                label="* Line of Business"
                value={
                  quote?.lob || {
                    value: 0,
                    label: "If applicable, assign to a Line of Business",
                  }
                }
                required={true}
                isInvalid={lobInvalid}
                options={lobOptions}
                onChange={(e) => {
                  if (e) {
                    setLobInvalid(false);
                    setQuote({ ...quote, lob: e as OptionType });
                  }
                }}
                isDisabled={disableFields}
              />
            </Col>
          </Row>
        </section>
        <hr style={{ marginRight: "10px" }} />
        <section>
          <Row style={{ alignItems: "center", marginRight: "1px" }}>
            <Col sm={8}>
              <div className="instructionalText bold">Pricing Details</div>
              {quote?.useServicePricing && (
                <div style={{ color: "#CC672F", margin: "-10px 0px 10px" }}>
                  <strong>Please Note: </strong> Service Pricing is in use
                </div>
              )}
            </Col>
          </Row>
          <Row style={{ marginRight: "1px" }}>
            <Col>
              <Select2V2
                label="Billing Method"
                value={quote?.billingMethod}
                options={[
                  { value: "time_and_materials", label: "Time and Materials" },
                  { value: "fixed_fee", label: "Fixed Fee" },
                ]}
                onChange={(e) => setQuote({ ...quote, billingMethod: e })}
                isDisabled={disableFields}
              />
            </Col>
            <Col>
              <Select2V2
                label="Includes Recurring Costs"
                value={quote?.billingFrequency}
                options={billingFrequencyOptions}
                onChange={(e) => setQuote({ ...quote, billingFrequency: e })}
                isDisabled={disableFields}
              />
            </Col>
          </Row>
          <Row style={{ marginRight: "1px" }}>
            <Col>
              <FormNumberFieldV2
                label="One Time Cost"
                min={0}
                step={0.01}
                value={quote?.oneTimeCost}
                onChange={(e) => {
                  let oneTimeCost = e.target.value;
                  setPriceOverride(false);
                  setQuote({
                    ...quote,
                    oneTimeCost,
                    oneTimeRevenue: calculatePrice(
                      oneTimeCost,
                      quote.markup,
                      revenueCalculationLabel
                    ),
                  });
                }}
                placeholder={"Enter one time cost"}
                readOnly={disableFields || lockPricingFields}
              />
            </Col>
            <Col>
              <FormNumberFieldV2
                label={marginVsMarkup === "margin" ? "Target Margin" : "Markup"}
                min={0}
                step={0.0001}
                value={quote?.markup}
                onChange={(e) => {
                  const markup = e.target.value;
                  setPriceOverride(false);
                  setQuote({
                    ...quote,
                    markup,
                    oneTimeRevenue: calculatePrice(
                      quote?.oneTimeCost,
                      markup,
                      revenueCalculationLabel
                    ),
                  });
                }}
                placeholder={`Enter ${
                  marginVsMarkup === "margin"
                    ? "target margin"
                    : "a markup percentage"
                }`}
                readOnly={disableFields || lockPricingFields}
              />
            </Col>
            <Col>
              <FormNumberFieldV2
                label="One Time Revenue"
                min={0}
                step={0.01}
                value={quote?.oneTimeRevenue}
                onChange={(e) => {
                  const oneTimeRevenue = e.target.value;
                  setPriceOverride(true);
                  setQuote({
                    ...quote,
                    oneTimeRevenue,
                    markup:
                      quote?.markup !== ""
                        ? calculateMarkup(
                            quote?.oneTimeCost,
                            oneTimeRevenue,
                            revenueCalculationLabel
                          )
                        : "",
                  });
                }}
                readOnly={disableFields || lockPricingFields}
              />
            </Col>
          </Row>
          {isRecurring && (
            <Row style={{ marginRight: "1px" }}>
              <Col>
                <FormNumberFieldV2
                  label={
                    quote?.billingFrequency?.value
                      ? `${snakeToText(quote.billingFrequency.value)} Cost`
                      : "Monthly Cost"
                  }
                  required={false}
                  min={0}
                  step={0.01}
                  value={quote?.recurringCost}
                  onChange={(e) => {
                    setMrrOverride(false);
                    const recurringCost = e.target.value;
                    setQuote({
                      ...quote,
                      recurringCost,
                      recurringRevenue: calculatePrice(
                        recurringCost,
                        quote?.mrrMarkup,
                        revenueCalculationLabel
                      ),
                    });
                  }}
                  placeholder={`Enter ${
                    quote?.billingFrequency?.value || "monthly"
                  } cost`}
                  readOnly={disableFields || lockPricingFields}
                />
              </Col>
              <Col>
                <FormNumberFieldV2
                  label={
                    marginVsMarkup === "margin" ? "Target Margin" : "Markup"
                  }
                  required={false}
                  min={0}
                  step={0.0001}
                  value={quote?.mrrMarkup}
                  onChange={(e) => {
                    setMrrOverride(false);
                    const mrrMarkup = e.target.value;
                    setQuote({
                      ...quote,
                      mrrMarkup,
                      recurringRevenue: calculatePrice(
                        quote?.recurringCost,
                        mrrMarkup,
                        revenueCalculationLabel
                      ),
                    });
                  }}
                  placeholder={`Enter ${
                    marginVsMarkup === "margin"
                      ? "target margin"
                      : "a markup percentage"
                  }`}
                  readOnly={disableFields || lockPricingFields}
                />
              </Col>
              <Col>
                <FormNumberFieldV2
                  label={
                    quote?.billingFrequency?.value
                      ? `${snakeToText(quote?.billingFrequency.value)} Revenue`
                      : "Monthly Revenue"
                  }
                  required={false}
                  min={0}
                  step={0.01}
                  value={quote?.recurringRevenue}
                  onChange={(e) => {
                    setMrrOverride(true);
                    const recurringRevenue = e.target.value;
                    setQuote({
                      ...quote,
                      recurringRevenue,
                      mrrMarkup: calculateMarkup(
                        quote?.recurringCost,
                        recurringRevenue,
                        revenueCalculationLabel
                      ).toString(),
                    });
                  }}
                  readOnly={disableFields || lockPricingFields}
                />
              </Col>
            </Row>
          )}
        </section>
      </Form>
    );
  }
);

export default QuoteForm;
